// src/components/routes/RouteForm.jsx
import React, { useEffect, useState } from "react";
import {
    Form, FormGroup, Label, Input, Row, Col, Button, FormFeedback, UncontrolledTooltip
} from "reactstrap";
import Session from '../auth/Session';
import { WT_ADT_ENABLED } from '../../featureFlags';

// Renders a field label whose text uses the "ADT" abbreviation, with a
// tooltip spelling out the full "Additional Drive Time" wording on hover.
const AdtLabel = ({ id, text, tip, check = false, className = "mb-1", ...rest }) => (
    <>
        <Label id={id} check={check} className={className} {...rest}>
            {text}{" "}
            <span className="text-muted" style={{ cursor: "help" }}>&#9432;</span>
        </Label>
        <UncontrolledTooltip target={id}>{tip}</UncontrolledTooltip>
    </>
);

const normalizeState = (value) => String(value || "").trim().toUpperCase();

const getDriverStates = (driver) => {
    const states = [
        ...(driver?.states || []),
        ...(driver?.stateAssignments || []).map(a => a.state),
    ];

    if (!states.length && driver?.state) {
        states.push(driver.state);
    }

    return [...new Set(states.map(normalizeState).filter(Boolean))];
};

const driverMatchesState = (driver, state) =>
    getDriverStates(driver).includes(normalizeState(state));

const RouteForm = ({ routeItem, apiBaseUrl, onSuccess, onCancel, routes }) => {
    const [formData, setFormData] = useState({
        id: 0,
        subCustomerId: "",
        name: "",
        quantity: 0,
        billingRate: 0,
        billingOvertimeRate: 0,
        payRate: 0,
        payOvertimeRate: 0,
        billingType: 'Full-day',
        wearAndTearRate: 0,
        payWearAndTearRate: 0,
        useSeparatePayWearAndTear: false,
        vehicleType: 'Pax Vehicle',
        transactionTypeId: null,
        driverId: null,
        deviceId: "",
        hasDeliveryRate: false,
        isParentRoute: true,
        parentRouteId: null,
        fullDayOT: 0,
        halfDayOT: 0,
        allowOvertime: false,
        roundOvertimeToHalfHour: true,
        deductLunch: false,
        adtRequiresAuthorization: false,
        isActive: true
    });

    const [subCustomers, setSubCustomers] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [transactionTypes, setTransactionTypes] = useState([]);
    const [subRoutes, setRoutes] = useState([]);

    const [errors, setErrors] = useState({});
    const subCustomersApi = "/api/subcustomers";
    const driversApi = "/api/drivers/list";
    const transactionTypeApi = "/api/TransactionType/GetTransactionTypesAsync";
    const routeApi = "/api/Routes/";

    useEffect(() => {
        console.log('route')
        console.log(routeItem);
        if (routeItem) {
            setFormData({
                id: routeItem.id,
                subCustomerId: routeItem.subCustomerId ?? "",
                name: routeItem.name ?? "",
                quantity: routeItem.quantity ?? 0,
                billingRate: Number(routeItem.billingRate ?? 0).toFixed(2),
                billingOvertimeRate: Number(routeItem.billingOvertimeRate ?? 0).toFixed(2),
                payRate: Number(routeItem.payRate ?? 0).toFixed(2),
                payOvertimeRate: Number(routeItem.payOvertimeRate ?? 0).toFixed(2),
                wearAndTearRate: Number(routeItem.wearAndTearRate ?? 0).toFixed(2),
                payWearAndTearRate: Number(routeItem.payWearAndTearRate ?? 0).toFixed(2),
                useSeparatePayWearAndTear: routeItem.useSeparatePayWearAndTear ?? false,
                vehicleType: routeItem.vehicleType ?? "Pax Vehicle",
                billingType: routeItem.billingType ?? "Full-day",
                transactionTypeId: routeItem.transactionTypeId ?? null,
                driverId: routeItem.driverId ?? null,
                deviceId: routeItem.deviceId ?? "",
                hasDeliveryRate: routeItem.hasDeliveryRate ?? false,
                isParentRoute: routeItem.isParentRoute ?? false,
                parentRouteId: routeItem.parentRouteId ?? null,
                fullDayOT: routeItem.fullDayOT ?? 0,
                halfDayOT: routeItem.halfDayOT ?? 0,
                allowOvertime: routeItem.allowOvertime ?? false,
                roundOvertimeToHalfHour: routeItem.roundOvertimeToHalfHour ?? true,
                deductLunch: routeItem.deductLunch ?? false,
                adtRequiresAuthorization: routeItem.adtRequiresAuthorization ?? false,
                isActive: routeItem.isActive ?? true
            });
        }
    }, [routeItem]);

    useEffect(() => {
        const loadData = async () => {
            const token = await Session.getToken();
            // Load dropdown data
            const [res, dRes, ttRes, rRes] = await Promise.all([
                fetch(subCustomersApi, { headers: { Authorization: `Bearer ${token}` } }),
                fetch(driversApi, { headers: { Authorization: `Bearer ${token}` } }),
                fetch(transactionTypeApi, { headers: { Authorization: `Bearer ${token}` } }),
                fetch(routeApi, { headers: { Authorization: `Bearer ${token}` } }),
            ]);


            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            const data = await res.json();
            setSubCustomers(data);
            setDrivers(dRes.ok ? await dRes.json() : []);
            setTransactionTypes(ttRes.ok ? await ttRes.json() : []);
            setRoutes(rRes.ok ? await rRes.json() : []);
        };
        loadData();
    }, []);

    //const handleChange = (e) => {
    //    const { name, value } = e.target;
    //    setFormData(p => ({ ...p, [name]: name.endsWith("Rate") || name === "quantity" ? Number(value) : value }));
    //};

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleChangeNumber = (e) => {
        const { name, value, type, checked } = e.target;
        var myValue = Number(Number(value).toFixed(2));
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : myValue,
        }));
    };

    const validate = () => {
        const v = {};
        if (!formData.name) {
            v.name = "Route name is required";
        } else {
            if (routes) {
                const trimmedName = (formData.name || '').trim().toLowerCase();
                const currentId = Number(formData.id) || 0;
                const matches = routes.filter(r => {
                    const rName = (r.name || '').trim().toLowerCase();
                    const rId = Number(r.id) || 0;
                    return rName === trimmedName && rId !== currentId;
                });
                if (matches.length > 0) {
                    v.name = 'Route name already exists!';
                }
            }
        }

        if (!formData.subCustomerId) v.subCustomerId = "Sub-Customer is required";
        //if (!formData.driverId) v.driverId = "Driver is required";
        setErrors(v);
        return Object.keys(v).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const data = {
            id: formData.id,
            subCustomerId: formData.subCustomerId ? Number(formData.subCustomerId) : null,
            name: formData.name,
            quantity: Number(formData.quantity) || 0,
            vehicleType: formData.vehicleType,
            billingRate: Number(formData.billingRate) || 0,
            billingType: formData.billingType,
            billingOvertimeRate: Number(formData.billingOvertimeRate) || 0,
            wearAndTearRate: Number(formData.wearAndTearRate) || 0,
            payWearAndTearRate: Number(formData.payWearAndTearRate) || 0,
            useSeparatePayWearAndTear: formData.useSeparatePayWearAndTear,
            payRate: Number(formData.payRate) || 0,
            payOvertimeRate: Number(formData.payOvertimeRate) || 0,
            transactionTypeId: formData.transactionTypeId ? Number(formData.transactionTypeId) : null,
            driverId: formData.driverId ? Number(formData.driverId) : null,
            deviceId: formData.deviceId,
            isParentRoute: formData.isParentRoute,
            parentRouteId: formData.parentRouteId ? Number(formData.parentRouteId) : null,
            hasDeliveryRate: formData.hasDeliveryRate,
            fullDayOT: Number(formData.fullDayOT) || 0,
            halfDayOT: Number(formData.halfDayOT) || 0,
            allowOvertime: formData.allowOvertime,
            roundOvertimeToHalfHour: formData.roundOvertimeToHalfHour,
            deductLunch: formData.deductLunch,
            adtRequiresAuthorization: formData.adtRequiresAuthorization,
            isActive: formData.isActive,
        };

        const method = `${routeItem.id}` !== `0` ? "PUT" : "POST";
        const url = `${routeItem.id}` !== `0` ? `${apiBaseUrl}/${formData.id}` : apiBaseUrl;

        const token = await Session.getToken();
        const res = await fetch(url, {
            method,
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
            body: JSON.stringify(data),
        });

        if (!res.ok) {
            const t = await res.text();
            alert(`Save failed: ${res.status} ${t}`);
            return;
        }
        onSuccess(await res.json());
    };

    return (
        <Form onSubmit={handleSubmit}>
            {/* Identity */}
            <Row>
                <Col md={6}>
                    <FormGroup className="mb-2">
                        <Label for="name" className="mb-1">Route Name</Label>
                        <Input bsSize="sm" id="name" name="name" value={formData.name} onChange={handleChange} invalid={!!errors.name} />
                        <FormFeedback>{errors.name}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup className="mb-2">
                        <Label for="deviceId" className="mb-1">Device ID</Label>
                        <Input bsSize="sm" id="deviceId" name="deviceId" value={formData.deviceId} onChange={handleChange} />
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <FormGroup className="mb-2">
                        <Label for="subCustomerId" className="mb-1">Sub-Customer</Label>
                        <Input
                            bsSize="sm"
                            id="subCustomerId"
                            name="subCustomerId"
                            type="select"
                            value={formData.subCustomerId}
                            onChange={handleChange}
                            invalid={!!errors.subCustomerId}
                        >
                            <option value="">-- Select Sub-Customer --</option>
                            {subCustomers.map(sc => (
                                <option key={sc.id} value={sc.id}>
                                    {sc.name} {sc.customer ? `— ${sc.customer.name}` : ""}
                                </option>
                            ))}
                        </Input>
                        <FormFeedback>{errors.subCustomerId}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup className="mb-2">
                        <Label for="driverId" className="mb-1">Driver</Label>
                        <Input
                            bsSize="sm"
                            id="driverId"
                            name="driverId"
                            type="select"
                            value={formData.driverId}
                            onChange={handleChange}
                        >
                            <option value="0">-- Select Driver --</option>
                            {drivers.filter(d => driverMatchesState(d, subCustomers.find(s => `${s.id}` === `${formData.subCustomerId}`)?.state)).map(d => (
                                <option key={d.id} value={d.id}>
                                    {d.driverName}{d.businessName ? ` — ${d.businessName}` : ""}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={3} xs={6}>
                    <FormGroup className="mb-2">
                        <Label for="vehicleType" className="mb-1">Vehicle Type</Label>
                        <Input
                            bsSize="sm"
                            id="vehicleType"
                            name="vehicleType"
                            type="select"
                            value={formData.vehicleType || ""}
                            onChange={handleChange}
                            invalid={!!errors.vehicleType}
                        >
                            <option value="">-- Vehicle Type --</option>
                            <option value="Pax Vehicle">Pax Vehicle</option>
                            <option value="Van">Van</option>
                        </Input>
                    </FormGroup>
                </Col>
                <Col md={3} xs={6}>
                    <FormGroup className="mb-2">
                        <Label for="billingType" className="mb-1">Rate Type</Label>
                        <Input
                            bsSize="sm"
                            id="billingType"
                            name="billingType"
                            type="select"
                            value={formData.billingType || ""}
                            onChange={handleChange}
                            invalid={!!errors.billingType}
                        >
                            <option value="">-- Rate Type --</option>
                            <option value="Full-day">Full-day</option>
                            <option value="Half-day">Half-day</option>
                            <option value="Hourly">Hourly</option>
                        </Input>
                    </FormGroup>
                </Col>
                <Col md={3} xs={6}>
                    <FormGroup className="mb-2">
                        <Label for="quantity" className="mb-1">Quantity (days/wk)</Label>
                        <Input bsSize="sm" id="quantity" name="quantity" type="number" value={formData.quantity} onChange={handleChange} />
                    </FormGroup>
                </Col>
                <Col md={3} xs={6}>
                    <FormGroup className="mb-2">
                        <Label for="transactionTypeId" className="mb-1">QB Transaction Type</Label>
                        <Input
                            bsSize="sm"
                            id="transactionTypeId"
                            name="transactionTypeId"
                            type="select"
                            value={formData.transactionTypeId || ""}
                            onChange={handleChange}
                            invalid={!!errors.transactionTypeId}
                        >
                            <option value="">-- Transaction Type --</option>
                            {transactionTypes.map(tt => (
                                <option key={tt.id} value={tt.id}>
                                    {tt.transactionName}
                                </option>
                            ))}
                        </Input>
                        <FormFeedback>{errors.transactionTypeId}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            {/* Rates */}
            <Row>
                <Col md={3} xs={6}>
                    <FormGroup className="mb-2">
                        <Label for="billingRate" className="mb-1">Billing Rate</Label>
                        <Input bsSize="sm" id="billingRate" name="billingRate" type="number" step="0.01" value={formData.billingRate} onChange={handleChange} onBlur={handleChangeNumber} />
                    </FormGroup>
                </Col>
                {WT_ADT_ENABLED && (
                <Col md={3} xs={6}>
                    <FormGroup className="mb-2">
                        <AdtLabel id="lblBillingAdt" for="billingOvertimeRate" text="Billing ADT" tip="Billing Additional Drive Time" />
                        <Input bsSize="sm" id="billingOvertimeRate" name="billingOvertimeRate" type="number" step="0.01" value={formData.billingOvertimeRate} onChange={handleChange} onBlur={handleChangeNumber} />
                    </FormGroup>
                </Col>
                )}
                <Col md={3} xs={6}>
                    <FormGroup className="mb-2">
                        <Label for="payRate" className="mb-1">Pay Rate</Label>
                        <Input bsSize="sm" id="payRate" name="payRate" type="number" step="0.01" value={formData.payRate} onChange={handleChange} onBlur={handleChangeNumber} />
                    </FormGroup>
                </Col>
                {WT_ADT_ENABLED && (
                <Col md={3} xs={6}>
                    <FormGroup className="mb-2">
                        <AdtLabel id="lblPayAdt" for="payOvertimeRate" text="Pay ADT" tip="Pay Additional Drive Time" />
                        <Input bsSize="sm" id="payOvertimeRate" name="payOvertimeRate" type="number" step="0.01" value={formData.payOvertimeRate} onChange={handleChange} onBlur={handleChangeNumber} />
                    </FormGroup>
                </Col>
                )}
            </Row>

            {WT_ADT_ENABLED && (
            <Row className="align-items-end">
                <Col md={3} xs={6}>
                    <FormGroup className="mb-2">
                        <Label for="wearAndTearRate" className="mb-1">Bill W&amp;T Charge</Label>
                        <Input bsSize="sm" id="wearAndTearRate" name="wearAndTearRate" type="number" step="0.01" value={formData.wearAndTearRate} onChange={handleChange} onBlur={handleChangeNumber} />
                    </FormGroup>
                </Col>
                <Col md={3} xs={6}>
                    <FormGroup className="mb-2">
                        <Label for="payWearAndTearRate" className="mb-1">Pay W&amp;T Charge</Label>
                        <Input
                            bsSize="sm"
                            id="payWearAndTearRate"
                            name="payWearAndTearRate"
                            type="number"
                            step="0.01"
                            value={formData.useSeparatePayWearAndTear ? formData.payWearAndTearRate : formData.wearAndTearRate}
                            disabled={!formData.useSeparatePayWearAndTear}
                            onChange={handleChange}
                            onBlur={handleChangeNumber}
                        />
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup check className="mb-2">
                        <Input
                            id="useSeparatePayWearAndTear"
                            name="useSeparatePayWearAndTear"
                            type="checkbox"
                            checked={formData.useSeparatePayWearAndTear}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                setFormData((prev) => ({
                                    ...prev,
                                    useSeparatePayWearAndTear: checked,
                                    payWearAndTearRate:
                                        checked && (!prev.payWearAndTearRate || Number(prev.payWearAndTearRate) === 0)
                                            ? prev.wearAndTearRate
                                            : prev.payWearAndTearRate,
                                }));
                            }}
                        />{" "}
                        <Label for="useSeparatePayWearAndTear" check>Use separate Pay W&amp;T Charge</Label>
                    </FormGroup>
                </Col>
            </Row>
            )}

            {/* Additional Drive Time (ADT) */}
            {WT_ADT_ENABLED && (
            <Row>
                <Col md={3} xs={6}>
                    <FormGroup className="mb-2">
                        <AdtLabel id="lblFullDayAdt" for="fullDayOT" text="Full Day ADT Cutoff (Hrs)" tip="Full Day Additional Drive Time Cutoff (Hours)" />
                        <Input bsSize="sm" id="fullDayOT" name="fullDayOT" type="number" step="0.01" value={formData.fullDayOT} onChange={handleChange} />
                    </FormGroup>
                </Col>
                <Col md={3} xs={6}>
                    <FormGroup className="mb-2">
                        <AdtLabel id="lblHalfDayAdt" for="halfDayOT" text="Half Day ADT Cutoff (Hrs)" tip="Half Day Additional Drive Time Cutoff (Hours)" />
                        <Input bsSize="sm" id="halfDayOT" name="halfDayOT" type="number" step="0.01" value={formData.halfDayOT} onChange={handleChange} />
                    </FormGroup>
                </Col>
                <Col md={3} xs={6} className="align-self-center">
                    <FormGroup check className="mb-2">
                        <Input
                            id="allowOvertime"
                            name="allowOvertime"
                            type="checkbox"
                            checked={formData.allowOvertime}
                            onChange={handleChange}
                        />{" "}
                        <AdtLabel id="lblAllowAdt" check for="allowOvertime" className="" text="Allows ADT" tip="Allows Additional Drive Time" />
                    </FormGroup>
                    <FormGroup check className="mb-2">
                        <Input
                            id="roundOvertimeToHalfHour"
                            name="roundOvertimeToHalfHour"
                            type="checkbox"
                            checked={formData.roundOvertimeToHalfHour}
                            onChange={handleChange}
                        />{" "}
                        <AdtLabel id="lblRoundAdt" check for="roundOvertimeToHalfHour" className="" text="Round ADT to Half-Hour" tip="Round Additional Drive Time to Half-Hour" />
                    </FormGroup>
                </Col>
                <Col md={3} xs={6} className="align-self-center">
                    <FormGroup check className="mb-2">
                        <Input
                            id="adtRequiresAuthorization"
                            name="adtRequiresAuthorization"
                            type="checkbox"
                            checked={formData.adtRequiresAuthorization}
                            onChange={handleChange}
                        />{" "}
                        <AdtLabel id="lblAdtAuth" check for="adtRequiresAuthorization" className="" text="ADT Requires Authorization" tip="Additional Drive Time Requires Authorization" />
                    </FormGroup>
                    <FormGroup check className="mb-2">
                        <Input
                            id="deductLunch"
                            name="deductLunch"
                            type="checkbox"
                            checked={formData.deductLunch}
                            onChange={handleChange}
                        />{" "}
                        <Label for="deductLunch" check>Deduct 30-min Lunch</Label>
                    </FormGroup>
                </Col>
            </Row>
            )}

            {/* Options & hierarchy */}
            <Row className="align-items-end">
                <Col md={6}>
                    {!formData.isParentRoute && (
                        <FormGroup className="mb-2">
                            <Label for="parentRouteId" className="mb-1">Parent Route</Label>
                            <Input
                                bsSize="sm"
                                id="parentRouteId"
                                name="parentRouteId"
                                type="select"
                                value={formData.parentRouteId ?? ""}
                                onChange={handleChange}
                                invalid={!!errors.parentRouteId}
                            >
                                <option value="">-- Select Parent Route --</option>
                                {subRoutes
                                    .filter(r => String(r.subCustomerId) === String(formData.subCustomerId)
                                        && r.id !== formData.id
                                        && r.isActive
                                        && r.isParentRoute === true)
                                    .map(r => (
                                        <option key={r.id} value={r.id}>
                                            {r.name}
                                        </option>
                                    ))}
                            </Input>
                            <FormFeedback>{errors.parentRouteId}</FormFeedback>
                        </FormGroup>
                    )}
                    {formData.isParentRoute && formData.id > 0 && (
                        <FormGroup className="mb-2">
                            <Label className="mb-1">Sub-Routes</Label>
                            {(() => {
                                const children = subRoutes.filter(r => r.parentRouteId === formData.id);
                                return children.length > 0 ? (
                                    <ul className="mb-0 ps-3">
                                        {children.map(c => (
                                            <li key={c.id}>
                                                {c.name}{c.isActive === false ? " (inactive)" : ""}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="text-muted small">No sub-routes assigned to this parent.</div>
                                );
                            })()}
                        </FormGroup>
                    )}
                </Col>
                <Col md={2} xs={4}>
                    <FormGroup check className="mb-2">
                        <Input
                            id="hasDeliveryRate"
                            name="hasDeliveryRate"
                            type="checkbox"
                            checked={formData.hasDeliveryRate}
                            onChange={handleChange}
                        />{" "}
                        <Label for="hasDeliveryRate" check>Uses Delivery Rate</Label>
                    </FormGroup>
                </Col>
                <Col md={2} xs={4}>
                    <FormGroup check className="mb-2">
                        <Input
                            id="isParentRoute"
                            name="isParentRoute"
                            type="checkbox"
                            checked={formData.isParentRoute}
                            onChange={handleChange}
                        />{" "}
                        <Label for="isParentRoute" check>Is Parent Route</Label>
                    </FormGroup>
                </Col>
                <Col md={2} xs={4}>
                    <FormGroup check className="mb-2">
                        <Input
                            id="isActive"
                            name="isActive"
                            type="checkbox"
                            checked={formData.isActive}
                            onChange={handleChange}
                        />{" "}
                        <Label for="isActive" check>Active</Label>
                    </FormGroup>
                </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-2">
                <Button color="secondary" type="button" onClick={onCancel}>Cancel</Button>
                <Button color="primary" type="submit">{`${routeItem.id}` !== `0` ? "Update" : "Create"}</Button>
            </div>
        </Form>
    );
};

export default RouteForm;
